import { useEffect, useRef, useState } from "react";
import { api, fileSrc } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Paperclip, Send, Image as ImageIcon, X, Crosshair } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { playChime } from "@/lib/sound";

const CHANNEL = "ar15";

function AttachmentView({ fileId, mine }) {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    api.get(`/files/${fileId}`).then(({ data }) => setMeta(data)).catch(() => {});
  }, [fileId]);
  if (!meta) return null;
  if (meta.is_image) {
    return <img src={fileSrc(fileId)} alt={meta.original_filename} className="mt-2 max-h-64 rounded-xl" />;
  }
  return (
    <a href={fileSrc(fileId)} target="_blank" rel="noreferrer"
       className={`mt-2 flex items-center gap-2 px-3 py-2 rounded-xl ${mine ? "bg-purple-900/40" : "bg-white"} text-sm`}>
      <Paperclip className="w-4 h-4" /><span className="truncate">{meta.original_filename}</span>
    </a>
  );
}

export default function AR15() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const lastSinceRef = useRef(null);

  const loadAll = async () => {
    try {
      const { data } = await api.get(`/chat?channel_id=${CHANNEL}`);
      setMessages(data);
      lastSinceRef.current = data.length ? data[data.length - 1].created_at : null;
    } catch (err) { console.error("AR-15 load failed:", err); }
  };

  const poll = async () => {
    if (!lastSinceRef.current) return;
    try {
      const { data } = await api.get(`/chat?channel_id=${CHANNEL}&since=${encodeURIComponent(lastSinceRef.current)}`);
      if (data.length) {
        setMessages((prev) => [...prev, ...data]);
        lastSinceRef.current = data[data.length - 1].created_at;
        if (data.some((m) => m.author_id !== user?.user_id)) playChime();
      }
    } catch (err) { console.error("AR-15 poll failed:", err); }
  };

  useEffect(() => {
    loadAll();
    const t = setInterval(poll, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachment(data);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !attachment) return;
    try {
      const { data } = await api.post("/chat", {
        text: text.trim(),
        attachment_file_id: attachment?.id || null,
        video_url: null,
        channel_id: CHANNEL,
      });
      setMessages((prev) => [...prev, data]);
      lastSinceRef.current = data.created_at;
      setText(""); setAttachment(null);
    } catch (er) {
      toast.error(er.response?.data?.detail || "Send failed");
    }
  };

  return (
    <div className="animate-fade-up" data-testid="ar15-page">
      <div className="mb-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-slate-900 flex items-center justify-center shrink-0">
          <Crosshair className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-amber-600 font-bold">Restricted group</p>
          <h1 className="text-3xl sm:text-4xl font-black font-display tracking-tight">AR-15</h1>
        </div>
      </div>

      <div className="relative flex flex-col h-[calc(100vh-14rem)] bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center">
          <img src="/cfaa-mark.png" alt="" className="w-full h-full opacity-100 object-cover" />
        </div>
        <div ref={scrollRef} className="relative z-10 flex-1 overflow-y-auto chat-scroll p-6 space-y-4" data-testid="ar15-message-list">
          {messages.length === 0 && (
            <div className="text-center text-slate-400 py-12">
              <p className="text-lg font-semibold">AR-15 group chat — start the conversation 🎯</p>
            </div>
          )}
          {messages.map((m) => {
            const mine = m.author_id === user?.user_id;
            return (
              <div key={m.id} className={`flex gap-3 animate-slide-in ${mine ? "flex-row-reverse" : ""}`}>
                <Avatar className="w-9 h-9 flex-shrink-0">
                  <AvatarImage src={m.author_picture} />
                  <AvatarFallback className="bg-amber-400 text-slate-900 font-bold text-sm">{m.author_name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className={`max-w-[70%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                  <div className="text-xs font-semibold text-slate-500 mb-1">{mine ? "You" : m.author_name}</div>
                  <div className={`px-4 py-2.5 rounded-2xl ${mine ? "bg-purple-700 text-white rounded-tr-sm" : "bg-slate-100 text-slate-900 rounded-tl-sm"}`}>
                    {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                    {m.attachment_file_id && <AttachmentView fileId={m.attachment_file_id} mine={mine} />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <form onSubmit={send} className="relative z-10 border-t border-slate-100 p-4 space-y-2">
          {attachment && (
            <div className="flex items-center gap-2 bg-purple-50 rounded-2xl px-3 py-2 text-sm">
              <Paperclip className="w-4 h-4 text-purple-700" />
              <span className="truncate flex-1">{attachment.original_filename}</span>
              <button type="button" onClick={() => setAttachment(null)} className="text-slate-400 hover:text-slate-700" data-testid="ar15-remove-attachment-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" className="hidden" onChange={handleFile} data-testid="ar15-file-input" />
            <Button type="button" variant="ghost" size="icon" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="ar15-attach-btn" className="rounded-full">
              <ImageIcon className="w-5 h-5 text-slate-500" />
            </Button>
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Message the AR-15 group…" data-testid="ar15-text-input" className="flex-1 h-12 rounded-2xl border-slate-200" />
            <Button type="submit" data-testid="ar15-send-btn" className="h-12 px-5 rounded-2xl bg-purple-700 hover:bg-purple-800 text-white btn-lift">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
